import type { FieldConfigType } from '@/shared/components/query-builder/types'
import type { DataChoicesRegistry, FilterField } from './types'
import type { FilterSchema } from './validation'

import React, { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { SavedFilterType } from '@/shared/generated'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'

import { FormProvider } from '../HookForm'
import RHFQueryBuilder from '../HookForm/RHFQueryBuilder'
import ReecentlyFilter from './RecentlyFilter'
import { filtersSchema } from './validation'

type FiltersCommonProps = {
	enableRecentFilters?: boolean
	filters?: FilterSchema[]
	dataChoices?: DataChoicesRegistry
	fields: FilterField[]
	onApply(rules: FilterSchema[]): void
	extendedFieldConfig?: FieldConfigType
	irremovableCount?: number
	enableReferencing?: boolean
	savedFiltersType?: SavedFilterType
	enableSaveFilters?: boolean
}
type FiltersPopoverContentProps = FiltersCommonProps & {
	handleClose(): void
}
type FiltersPopoverProps = FiltersCommonProps & {
	children: React.ReactElement
	align?: 'start' | 'center' | 'end'
	side?: 'bottom' | 'top' | 'left' | 'right'
}

function FiltersPopoverContent({
	filters,
	dataChoices = {},
	fields = [],
	onApply,
	handleClose,
	enableSaveFilters,
	enableRecentFilters,
	extendedFieldConfig,
	irremovableCount,
	enableReferencing,
	savedFiltersType,
}: FiltersPopoverContentProps) {
	const { t } = useTranslation()

	const methods = useForm({
		values: {
			filters: filters || [],
		},
		defaultValues: {
			filters: [],
		},
		resolver: zodResolver(z.object({ filters: filtersSchema })),
	})
	const { showModal } = useModals()
	const handleCreateGlobalFilter = () =>
		savedFiltersType &&
		showModal('CREATE_ASSORTMENT_REPORT', {
			type: SavedFilterType.Products,
			filters: methods.getValues('filters') || [],
		})
	const handelApplyClick = (data: { filters: FilterSchema[] }) => {
		onApply(data.filters)
		handleClose()
	}
	const handelClearClick = () => {
		onApply([])
		handleClose()
	}

	return (
		<FormProvider
			methods={methods}
			onSubmit={methods.handleSubmit(handelApplyClick)}
		>
			<div className='grid gap-1.5 p-4'>
				{enableRecentFilters && <ReecentlyFilter />}
				<RHFQueryBuilder
					label={t('app.filters', { defaultValue: 'Filters' })}
					name='filters'
					fields={fields}
					dataChoices={dataChoices}
					irremovableCount={irremovableCount}
					extendedFieldConfig={extendedFieldConfig}
					enableReferencing={enableReferencing}
				/>
			</div>
			<div
				className={cn('flex items-center gap-2 px-4 pb-4', {
					'justify-between': enableSaveFilters,
					'justify-end': !enableSaveFilters,
				})}
			>
				<Button onClick={handelClearClick} data-testid='filters-popover-clear'>
					{t('general_clear')}
				</Button>
				<div className='flex items-center space-x-2'>
					{enableSaveFilters && (
						<Button
							onClick={handleCreateGlobalFilter}
							data-testid='filters-popover-save'
						>
							{t('general_save_filters')}
						</Button>
					)}
					<Button
						variant='primary'
						onClick={methods.handleSubmit(handelApplyClick)}
						data-testid='filters-popover-apply'
					>
						{t('general_apply')}
					</Button>
				</div>
			</div>
		</FormProvider>
	)
}
export default function FiltersPopover({
	children,
	align = 'start',
	side = 'bottom',
	enableReferencing = true,
	...props
}: FiltersPopoverProps) {
	const [open, setOpen] = useState(false)

	return (
		<Popover onOpenChange={setOpen} open={open} modal>
			<PopoverTrigger asChild>{children}</PopoverTrigger>
			<PopoverContent align={align} side={side} className='w-187 p-0'>
				<FiltersPopoverContent
					{...props}
					enableReferencing={enableReferencing}
					handleClose={() => setOpen(false)}
				/>
			</PopoverContent>
		</Popover>
	)
}
